// pf/tickets.ts — what the game knows of its pf tickets (SC-65843), from the SDK's two provably-fair events:
//   pf.ticket  a STATE (replayed to a late subscriber): the ticket held for the NEXT bet — its commitment, its accepted
//              terms, the client seed the next bet will use — or "legacy" (the server has not put Dice on tickets) or
//              "unavailable" (no ticket can be held right now);
//   pf.proof   one per ticket round once it ended (settled, voided, or its ticket retired), with the SDK's binding check.
// The Provably fair door reads both; nothing here decides money or the round. A FUN demo (the in-page platform) has no
// ticket routes: the SDK then emits nothing and exposes no pf controls — `state()` stays null.
import { PF_EVENT, type GameSdk, type PfProofPayload, type PfTicketPayload } from "@blocklabsdev/rgs-game-manager-frontend-api";

export interface PfTicketTracker {
  /** Where the tickets stand now: the SDK's current() when it has pf controls (never a ticket already spent), else the
   *  latest pf.ticket state, or null while the SDK has said none (a FUN demo). */
  state(): PfTicketPayload | null;
  /** The proofs heard, newest first (at most `limit`). */
  proofs(): readonly PfProofPayload[];
  /** The proof of the round played under `clientRoundId`, when one was heard. */
  proofFor(clientRoundId: string): PfProofPayload | undefined;
  /** The newest proof of one attempt (its stable id): a reveal that came later than the round replaces the first proof,
   *  which may not name the round id. */
  proofForAttempt(attemptId: string): PfProofPayload | undefined;
  /** The player's own client seed for the next bet (hex, single-use): false when it is not one, or this device used it,
   *  or the game is not on tickets. */
  setNextClientSeed(seed: string): Promise<boolean>;
  /** Called after every change (a new state, a new proof). Returns the unsubscribe. */
  onChange(listener: () => void): () => void;
}

/** SC-68793: the ticket held for the next bet names a math this build does not know (`known`: the pinned identity and its
 *  listed predecessors). A round on it could be played and never verified, so no bet is made on it. False when no ticket is
 *  held (a legacy game, none right now): that bet is the SDK's to decide. */
export function heldTicketMathUnknown(state: PfTicketPayload | null | undefined, known: readonly string[]): boolean {
  return state?.mode === "tickets" && !known.includes(state.ticket.mathId);
}

export function trackPfTickets(sdk: Pick<GameSdk, "on"> & { readonly pf?: GameSdk["pf"] }, limit = 400): PfTicketTracker {
  let state: PfTicketPayload | null = null;
  const proofs: PfProofPayload[] = [];
  const listeners = new Set<() => void>();
  const changed = (): void => {
    for (const l of [...listeners]) l();
  };
  sdk.on(PF_EVENT.TICKET, (payload) => {
    state = payload;
    changed();
  });
  sdk.on(PF_EVENT.PROOF, (payload) => {
    // A proof that became final (the reveal after a proof_outstanding one) replaces the earlier one of its attempt.
    const at = proofs.findIndex((p) => p.evidence.attempt.attemptId === payload.evidence.attempt.attemptId);
    if (at >= 0) proofs.splice(at, 1);
    proofs.unshift(payload);
    if (proofs.length > limit) proofs.length = limit;
    changed();
  });
  return {
    // The SDK's own current() is the truth (a spent ticket is cleared there before the next one arrives); the last
    // pf.ticket payload is what is left when the SDK exposes no controls.
    state: () => (sdk.pf ? sdk.pf.current() : state),
    proofs: () => proofs,
    proofFor: (clientRoundId) => proofs.find((p) => p.clientRoundId === clientRoundId),
    proofForAttempt: (attemptId) => proofs.find((p) => p.evidence.attempt.attemptId === attemptId),
    setNextClientSeed: async (seed) => (sdk.pf ? sdk.pf.setNextClientSeed(seed) : false),
    onChange: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
